import { Injectable } from '@angular/core';
import { CommonService } from 'src/app/services/common.service';
import {
  CaseAuditDateGroup,
  CaseAuditRaw,
  CaseAuditViewItem,
  ParsedAuditPayload,
} from './caseaudit.models';
import { CaseAuditPayloadParserService } from './caseaudit-payload-parser.service';

@Injectable({ providedIn: 'root' })
export class CaseAuditDataService {
  constructor(
    private parser: CaseAuditPayloadParserService,
    private common: CommonService
  ) {}

  parseDate(dateStr?: string | null): Date | null {
    if (!dateStr || typeof dateStr !== 'string') {
      return null;
    }
    const [datePart, timePart = '00:00'] = dateStr.trim().split(/\s+/);
    const dateSeparator = datePart.includes('/') ? '/' : '-';
    const [month, day, year] = datePart.split(dateSeparator).map(Number);
    const [hours = 0, minutes = 0, seconds = 0] = timePart.split(':').map(Number);
    if (![month, day, year, hours, minutes].every(Number.isFinite)) {
      return null;
    }
    const parsedDate = new Date(year, month - 1, day, hours, minutes, seconds || 0);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  /**
   * GetCaseAuditData returns AuditDate in UTC (MM/dd/yyyy HH:mm).
   * Convert to the header-selected timezone for display, grouping, and sorting.
   */
  toDisplayAuditDate(rawAuditDate?: string | null): string {
    if (!rawAuditDate || typeof rawAuditDate !== 'string') {
      return rawAuditDate || '';
    }
    if (!this.common.isHubApplication()) {
      return rawAuditDate;
    }
    const converted = this.common.formatDateTimeForDisplay(
      rawAuditDate.trim(),
      'MM/dd/yyyy HH:mm',
      'UTC'
    );
    return converted || rawAuditDate;
  }

  normalizeAuditType(auditType: string | null | undefined): string {
    if (!auditType || !String(auditType).trim()) {
      return 'Cases';
    }
    return String(auditType).trim();
  }

  /** Cases filter includes Cases, CaseVersions, and empty AuditType. */
  toFilterType(auditType: string): 'Cases' | 'Interface' {
    return auditType === 'Interface' ? 'Interface' : 'Cases';
  }

  formatDateLabel(date: Date): string {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  /** Short weekday for date filter dropdown, e.g. "Wed, July 15, 2026". */
  formatDateFilterLabel(date: Date): string {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  /** Title-case each word so "result Processed" → "Result Processed". */
  formatActionPerformed(value: string | null | undefined): string {
    if (!value || typeof value !== 'string') {
      return '';
    }
    return value
      .trim()
      .split(/\s+/)
      .map((word) =>
        word.length === 0
          ? word
          : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(' ');
  }

  /** True when ActionDetails Status value contains "fail" (e.g. Failed, Failure). */
  hasFailedStatus(actionDetails: ParsedAuditPayload, raw?: string): boolean {
    if (actionDetails?.pairs?.length) {
      for (const pair of actionDetails.pairs) {
        if (String(pair.name || '').trim().toLowerCase() !== 'status') {
          continue;
        }
        if (String(pair.value ?? '').toLowerCase().includes('fail')) {
          return true;
        }
      }
    }
    if (raw && typeof raw === 'string') {
      const match = /(?:^|;)\s*Status\s*:\s*([^;]+)/i.exec(raw);
      if (match && String(match[1]).toLowerCase().includes('fail')) {
        return true;
      }
    }
    return false;
  }

  /** Extract Interface value from AdditionalDetails pairs or raw string. */
  extractInterfaceName(
    additionalDetails: ParsedAuditPayload,
    raw?: string
  ): string {
    if (additionalDetails?.pairs?.length) {
      for (const pair of additionalDetails.pairs) {
        if (String(pair.name || '').trim().toLowerCase() === 'interface') {
          const val = pair.value == null ? '' : String(pair.value).trim();
          if (val) {
            return val;
          }
        }
      }
    }
    if (raw && typeof raw === 'string') {
      const match = /(?:^|;)\s*Interface\s*:\s*([^;]+)/i.exec(raw);
      if (match) {
        return String(match[1]).trim();
      }
    }
    return '';
  }

  async buildViewItems(raw: CaseAuditRaw[]): Promise<CaseAuditViewItem[]> {
    const withDisplay = (raw || []).map((row) => {
      const displayDate = this.toDisplayAuditDate(row.AuditDate);
      return { row, displayDate, parsed: this.parseDate(displayDate) };
    });

    const sorted = [...withDisplay].sort((a, b) => {
      const timeA = a.parsed?.getTime();
      const timeB = b.parsed?.getTime();
      if (timeA == null && timeB == null) {
        return 0;
      }
      if (timeA == null) {
        return 1;
      }
      if (timeB == null) {
        return -1;
      }
      return timeB - timeA; // descending
    });

    const items: CaseAuditViewItem[] = [];
    for (let i = 0; i < sorted.length; i++) {
      const { row, displayDate, parsed: parsedDate } = sorted[i];
      const dateKey =
        parsedDate != null
          ? `${parsedDate.getFullYear()}-${String(
              parsedDate.getMonth() + 1
            ).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`
          : displayDate?.split(/\s+/)[0] || '';
      const time =
        displayDate && displayDate.includes(' ')
          ? displayDate.split(/\s+/).slice(1).join(' ')
          : '';

      const actionDetails = await this.parser.parsePayload(row.ActionDetails);
      const additionalDetails = await this.parser.parsePayload(
        row.AdditionalDetails
      );
      const auditType = this.normalizeAuditType(row.AuditType);
      const networkPaths = this.parser.extractNetworkPaths(
        row.ActionDetails,
        row.AdditionalDetails
      );
      const caseFileRefs = this.parser.extractCaseFileRefs(
        row.ActionDetails,
        row.AdditionalDetails
      );

      const hasExpandablePayload =
        actionDetails.kind !== 'text' ||
        additionalDetails.kind !== 'text' ||
        (actionDetails.kind === 'text' &&
          !!actionDetails.text &&
          actionDetails.text.length > 80) ||
        networkPaths.length > 0 ||
        caseFileRefs.length > 0 ||
        !!row.AdditionalDetails?.trim();

      items.push({
        id: i,
        auditDateRaw: row.AuditDate,
        auditDate: displayDate,
        dateKey,
        time,
        actionPerformed: this.formatActionPerformed(row.ActionPerformed),
        interfaceName: this.extractInterfaceName(
          additionalDetails,
          row.AdditionalDetails
        ),
        performedBy: row.PerformedBy,
        auditType,
        filterType: this.toFilterType(auditType),
        actionDetailsRaw: row.ActionDetails || '',
        additionalDetailsRaw: row.AdditionalDetails || '',
        actionDetails,
        additionalDetails,
        hasExpandablePayload,
        networkPaths,
        caseFileRefs,
        hasFailedStatus: this.hasFailedStatus(
          actionDetails,
          row.ActionDetails
        ),
        expanded: false,
      });
    }
    return items;
  }

  groupByDate(items: CaseAuditViewItem[]): CaseAuditDateGroup[] {
    const map = new Map<string, CaseAuditViewItem[]>();
    for (const item of items) {
      const key = item.dateKey || 'Unknown';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(item);
    }

    const groups: CaseAuditDateGroup[] = [];
    for (const [dateKey, groupItems] of map.entries()) {
      const first = groupItems[0];
      const parsed = this.parseDate(first?.auditDate);
      groups.push({
        dateKey,
        label: parsed
          ? this.formatDateLabel(parsed)
          : dateKey,
        expanded: true,
        items: groupItems,
      });
    }

    // Newest date sections first
    return groups.sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }

  getDistinctPerformedBy(items: CaseAuditViewItem[]): string[] {
    const set = new Set<string>();
    for (const item of items) {
      if (item.performedBy) {
        set.add(item.performedBy);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  /** Distinct Category (AuditType) values from loaded data. */
  getDistinctCategories(items: CaseAuditViewItem[]): string[] {
    const set = new Set<string>();
    for (const item of items) {
      if (item.auditType) {
        set.add(item.auditType);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  /** Distinct dates from data, newest first. */
  getDistinctDates(
    items: CaseAuditViewItem[]
  ): Array<{ dateKey: string; label: string }> {
    const map = new Map<string, string>();
    for (const item of items) {
      const key = item.dateKey || '';
      if (!key || map.has(key)) {
        continue;
      }
      const parsed = this.parseDate(item.auditDate);
      map.set(key, parsed ? this.formatDateFilterLabel(parsed) : key);
    }
    return Array.from(map.entries())
      .map(([dateKey, label]) => ({ dateKey, label }))
      .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }

  applyFilters(
    items: CaseAuditViewItem[],
    typeFilter: string,
    performedBy: string,
    dateKey: string = 'All'
  ): CaseAuditViewItem[] {
    return items.filter((item) => {
      if (typeFilter && typeFilter !== 'All' && item.auditType !== typeFilter) {
        return false;
      }
      if (performedBy && performedBy !== 'All' && item.performedBy !== performedBy) {
        return false;
      }
      if (dateKey && dateKey !== 'All' && item.dateKey !== dateKey) {
        return false;
      }
      return true;
    });
  }
}
